import React from "react";
import { useLocalization } from "utilities/hooks/use-localization";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import UserCollectionShareType from "models/enumerations/user-collection-share-type";
import MyBookmarksAccordionPanel from "../my-bookmarks-dashboard/my-bookmarks-accordion-panel";
import { UserBookmarkCountBy } from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-by";
import Heading from "atoms/typography/heading";
import { HeadingPriority } from "atoms/constants/heading-priority";
import Paragraph from "atoms/typography/paragraph";
import Icon from "atoms/icons/icon";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import useUserCollection from "utilities/hooks/domain/users/user-collections/use-user-collection";
import UserBookmarkCountRecord from "models/view-models/user-bookmark-count-record";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import useModalActions from "utilities/hooks/use-modal-actions";
import UpdateUserCollectionModal from "../user-collections/update-user-collection-modal";
import UserCollectionRecord from "models/view-models/user-collection-record";
import UserCollectionShareStatus from "molecules/user-collection-share-status/user-collection-share-status";
export interface CollectionItemProps {
    bookmarkCount: UserBookmarkCountRecord;
    onUpdateCollection: () => void;
}
const BASE_CLASS_NAME = "c-collection-item";

const CollectionItem = (props: CollectionItemProps) => {
    const { bookmarkCount, onUpdateCollection } = props;

    const { globalState } = useGlobalState();
    const { t } = useLocalization();
    const currentUserId = globalState.currentIdentity?.userId();

    const {
        isOpen: manageCollectionModalIsOpen,
        handleOpen: handleOpenManageCollectionModal,
        handleClose: handleCloseManageCollectionModal,
    } = useModalActions();

    const { resultObject: userCollection, refresh: refreshCollection } =
        useUserCollection(currentUserId, Number(bookmarkCount.id));

    const canWriteCollection = userCollection?.userId === currentUserId;

    const handleOnUserCollectionSave = () => {
        onUpdateCollection();
        handleCloseManageCollectionModal();
        refreshCollection();
    };

    const handleEditButtonClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.stopPropagation();
        handleOpenManageCollectionModal();
    };

    const collectionHeader = (
        <div className={`${BASE_CLASS_NAME}__header`}>
            <Icon type={Icons.FolderLight} size={IconSizes.Large} />
            <div className={`${BASE_CLASS_NAME}__text`}>
                <Heading
                    dataTestId="collection-item--heading"
                    cssClassName={`${BASE_CLASS_NAME}__title`}
                    priority={HeadingPriority.Four}>
                    {bookmarkCount.name}
                    {canWriteCollection && (
                        <Button
                            dataTestId="collection-item--edit-button"
                            style={ButtonStyles.None}
                            cssClassName={`${BASE_CLASS_NAME}__editButton`}
                            onClick={handleEditButtonClick}>
                            <Icon
                                cssClassName={`${BASE_CLASS_NAME}__editButtonIcon`}
                                type={Icons.Edit}
                            />
                        </Button>
                    )}
                </Heading>
                <Paragraph
                    cssClassName={`${BASE_CLASS_NAME}__bookmarkCountSubtitle`}>
                    {t(
                        userCollection?.userBookmarkCount === 1
                            ? "bookmarkWithCount"
                            : "bookmarkWithCount_plural",
                        { count: userCollection?.userBookmarkCount }
                    )}
                </Paragraph>
            </div>
            {userCollection?.shareType !== UserCollectionShareType.User && (
                <UserCollectionShareStatus userCollection={userCollection!} />
            )}
        </div>
    );

    return (
        <div className={`${BASE_CLASS_NAME}`}>
            <MyBookmarksAccordionPanel
                id={bookmarkCount.id}
                header={collectionHeader}
                groupBy={UserBookmarkCountBy.Collection}
                filterBookmarksBySearch={false}
            />
            <UpdateUserCollectionModal
                closeDialog={handleCloseManageCollectionModal}
                isVisible={manageCollectionModalIsOpen}
                userCollection={userCollection ?? new UserCollectionRecord()}
                onConfirm={handleOnUserCollectionSave}
            />
        </div>
    );
};

export default CollectionItem;
